import React, { useState } from 'react'
import { useTranslation } from 'language/translator'

import {
  InnerWidgetPatchBody,
  isCoreWidget,
  TSortByOption,
  TWidgetSettingsLoaded,
  VisualizationConstraint,
  WidgetClass,
  WidgetType
} from 'types/GlobalWidget'

import {
  useAttributeOptions,
  useCommonAttributeOptions,
  useSegmentBy
} from 'redux/hooks/AttributeOptions'

import Button from 'components_new/atoms/Button'
import Switch from 'components_new/atoms/Switch'
import Text from 'components_new/atoms/Text'

import { AiIconTip } from 'components_new/molecules/IconTip'
import SelectSearch, { Option } from 'components_new/molecules/SelectSearch'
import SettingsDrawer from 'components_new/molecules/SettingsDrawer'
import SettingsGroup from 'components_new/molecules/SettingsGroup'
import SettingsItem from 'components_new/molecules/SettingsItem'
import SettingsEditDialog from 'components_new/molecules/SettingsEditDialog'
import SimpleDialog from 'components_new/molecules/SimpleDialog'
import ToggleButton from 'components_new/molecules/ToggleButton'

import AdditionalSegmentSection from './AdditionalSegmentSection'
import VariantOptionSection, {
  useVariantCatalogue
} from './VariantOptionSection'

import {
  ComparativeDisplayType,
  RollingType,
  UnitPrefix
} from 'types/GlobalKpiOption'
import { ShowPerEnum } from 'types/GlobalMetric'
import { SegmentSource } from 'types/GlobalVariantOption'

import { TInputType } from 'components_new/molecules/SettingsEditDialog/utils'

import { getSegmentByOptions, getSortOrderOptions } from './utils'

interface SegmentSectionProps {
  createAdditionalSegmentBy: (attributeOptionId: string) => void
  createVariantOptionBy: (variantKpiTemplateId: string, title: string) => void
  deleteAdditionalSegmentBy: (id: string) => void
  deleteVariantOptionBy: (id: string) => void
  onClose: () => void
  updateAdditionalSegmentBy: (id: string, attributeOptionId: string) => void
  updateVariantOptionBy: (
    id: string,
    body: {
      active_show_per?: ShowPerEnum | null
      unit_prefix?: UnitPrefix | null
      title?: string
      number_of_decimals?: number
      rolling_active?: RollingType | null
      comparative_display_type?: ComparativeDisplayType
    }
  ) => void
  updateWidget: (body: InnerWidgetPatchBody) => void
  widget: TWidgetSettingsLoaded
}

const LEGAL_TYPES = [
  WidgetType.BAR_CHART,
  WidgetType.BUBBLE_CHART,
  WidgetType.COMBO_CHART,
  WidgetType.FUNNEL,
  WidgetType.LIST,
  WidgetType.PIE_CHART,
  WidgetType.LINE_CHART,
  WidgetType.FUNNEL
]

const SegmentSection = (props: SegmentSectionProps) => {
  const {
    createAdditionalSegmentBy,
    createVariantOptionBy,
    deleteAdditionalSegmentBy,
    deleteVariantOptionBy,
    onClose,
    updateAdditionalSegmentBy,
    updateVariantOptionBy,
    updateWidget,
    widget
  } = props

  const [limitationsDrawer, setLimitationsDrawer] = useState<boolean>(false)
  const [segmentByDrawer, setSegmentByDrawer] = useState<boolean>(false)
  const [sortByDrawer, setSortByDrawer] = useState<boolean>(false)
  const [sourceDialog, setSourceDialog] = useState<{
    open: boolean
    source: SegmentSource
    attributeId: string
  }>({
    open: false,
    source: SegmentSource.ATTRIBUTE,
    attributeId: ''
  })
  const [dialog, setDialog] = useState<{
    open: boolean
    property: TProperty
    title: string
    data: {
      options: Option[]
      value: any
      type: TInputType
    }
  }>({
    open: false,
    property: 'segment_by',
    title: '',
    data: {
      options: [],
      value: '',
      type: 'text'
    }
  })
  const { t, lang } = useTranslation()

  const attributeOptions = useAttributeOptions()
  const segmentBy = useSegmentBy(widget.settings)
  const segmentAttributeOptions = useCommonAttributeOptions(
    widget,
    'segment_attribute'
  )
  const { variants: variantCatalogue, defaultVariantId } =
    useVariantCatalogue(widget)

  if (
    !LEGAL_TYPES.includes(widget.settings.type.selected) ||
    !widget.settings.segment_by.allow_additional_segments
  ) {
    return null
  }

  const getSegmentTitle = (
    type: Omit<
      WidgetType,
      WidgetType.KEY_FIGURE | WidgetType.LINE_CHART | WidgetType.MAP
    >
  ) => {
    switch (type) {
    case WidgetType.BAR_CHART:
      return t('widgets', 'bars')
    case WidgetType.BUBBLE_CHART:
      return t('widgets', 'bubbles')
    case WidgetType.LIST:
      // a horizontal list lays its segments out as columns
      return widget.settings.horizontal
        ? t('global', 'columns')
        : t('widgets', 'rows')
    case WidgetType.COMBO_CHART:
      return t('widgets', 'bars_and_lines')
    case WidgetType.PIE_CHART:
      return t('widgets', 'pie_segments')
    case WidgetType.FUNNEL:
      return t('widgets', 'steps')
    default:
      return ''
    }
  }

  // comparison columns replace the segmentation as columns, so the
  // segment settings step aside while any exist
  const variantMode = widget.settings.variant_options.length > 0

  const selectedLabel = variantMode
    ? t('widgets', 'variant_options_mode_variants')
    : segmentBy?.name ?? ''

  /*-- expanded segment levels --*/

  // the levels a table's rows nest in, top level first: a horizontal
  // table starts from its metrics and one with comparison figures
  // from those, before the additional segments
  const rowLevelNames = [
    widget.settings.horizontal ? t('widgets', 'metric') : selectedLabel,
    ...widget.settings.segment_by.additional.map(
      (additional) =>
        attributeOptions[additional.attribute_option_id]?.name ?? ''
    )
  ]
  const lastRowLevel = rowLevelNames.length - 1
  const expandedSegmentLevels = Math.min(
    widget.settings.expanded_segment_levels,
    lastRowLevel
  )
  const expandedSegmentLevelOptions: Option[] = rowLevelNames.map(
    (name, level) => {
      let title = name

      if (level === 0) {
        title = t('widgets', 'expanded_segment_levels_all_collapsed', false, {
          name
        })
      } else if (level === lastRowLevel) {
        title = t('widgets', 'expanded_segment_levels_all_expanded', false, {
          name
        })
      }

      return { id: level.toString(), title }
    }
  )

  /*-- sorting labels --*/
  const selectedSortByLabel =
    widget.settings.sort_by.options.find(
      (option) => option.value === widget.settings.sort_by.selected
    )?.label ?? ''
  const sortOrderOptions = getSortOrderOptions(
    widget.settings.sort_by.type,
    widget.settings.sort_by.allow_sort_on_kpi_variable
  )
  const selectedSortOrderLabel =
    sortOrderOptions.find(
      (option) => option.id === widget.settings.sort_by.visualization_constraint
    )?.title || ''
  const sortingLabel = `${selectedSortByLabel}, ${selectedSortOrderLabel}`

  /*-- drawers --*/
  const handleBackLimitationsDrawer = () => {
    setLimitationsDrawer(false)
  }

  const handleCloseLimitationsDrawer = () => {
    handleBackLimitationsDrawer()
    onClose()
  }

  const handleOpenLimitationsDrawer = () => {
    setLimitationsDrawer(true)
  }

  const handleBackSegmentByDrawer = () => {
    setSegmentByDrawer(false)
  }

  const handleCloseSegmentByDrawer = () => {
    handleBackSegmentByDrawer()
    onClose()
  }

  const handleOpenSegmentByDrawer = () => {
    setSegmentByDrawer(true)
  }

  /*-- column source --*/
  const variantOptionsEligible = variantCatalogue.length > 0

  const handleOpenSourceDialog = () => {
    setSourceDialog({
      open: true,
      source: variantMode ? SegmentSource.VARIANTS : SegmentSource.ATTRIBUTE,
      attributeId: widget.settings.segment_by.selected ?? ''
    })
  }

  const handleCloseSourceDialog = () => {
    setSourceDialog((prevState) => ({ ...prevState, open: false }))
  }

  // entering the comparison figure mode needs a variant to start from
  const entersVariantMode =
    sourceDialog.source === SegmentSource.VARIANTS &&
    !variantMode &&
    !!defaultVariantId

  // leaving the variant option mode means segmenting by an attribute
  // again, so it needs one to set
  const leavesVariantMode =
    sourceDialog.source === SegmentSource.ATTRIBUTE &&
    variantMode &&
    !!sourceDialog.attributeId

  const changesAttribute =
    sourceDialog.source === SegmentSource.ATTRIBUTE &&
    !!sourceDialog.attributeId &&
    sourceDialog.attributeId !== widget.settings.segment_by.selected

  // saving is only meaningful when the dialog changes something
  const sourceDialogHasChange =
    entersVariantMode || leavesVariantMode || changesAttribute

  const handleSubmitSourceDialog = () => {
    if (sourceDialog.source === SegmentSource.VARIANTS) {
      // switching to comparison figures starts from the outcome; the
      // segments are then managed in the list
      const defaultVariant = variantCatalogue.find(
        (variant) => variant.id === defaultVariantId
      )

      if (!variantMode && defaultVariant) {
        createVariantOptionBy(defaultVariant.id, defaultVariant.title[lang])
      }
    } else if (
      sourceDialog.attributeId &&
      (leavesVariantMode || changesAttribute)
    ) {
      // setting the segmentation is what leaves the variant option
      // mode, so it is sent even when the attribute is unchanged
      updateWidget({ segment_by: sourceDialog.attributeId })
    }

    handleCloseSourceDialog()
  }

  const handleBackSortByDrawer = () => {
    setSortByDrawer(false)
  }

  const handleCloseSortByDrawer = () => {
    handleBackSortByDrawer()
    onClose()
  }

  const handleOpenSortByDrawer = () => {
    setSortByDrawer(true)
  }

  /*-- dialogs --*/

  // Properties edited in this component.
  type TProperty =
    | 'expanded_segment_levels'
    | 'label_chars'
    | 'limitations'
    | 'limit_selection_to_primary_kpi'
    | 'segment_by'
    | 'segment_by_display_name'
    | 'sort_by'
    | 'sorting'
    | 'top_n_limit'
    | 'visualization_constraint'

  const getPropertyTitle = (property: TProperty) => {
    switch (property) {
    case 'expanded_segment_levels':
      return t('widgets', 'expanded_segment_levels_title')
    case 'label_chars':
      return t('widgets', 'label_chars_title')
    case 'limit_selection_to_primary_kpi':
      return t('widgets', 'limit_selection_to_primary_kpi_title')
    case 'segment_by':
      return t('widgets', 'segment_by_title')
    case 'segment_by_display_name':
      return t('widgets', 'segment_alias')
    case 'top_n_limit':
      return t('widgets', 'amount_of', false, {
        name: getSegmentTitle(widget.settings.type.selected).toLowerCase()
      })
    case 'limitations':
      return t('global', 'restrict')
    case 'sorting':
      return t('widgets', 'sorting_title')
    case 'sort_by':
      return t('widgets', 'sort_by_title')
    case 'visualization_constraint':
      return t('widgets', 'visualization_constraint_title')
    }
  }

  const handleCloseDialog = () => {
    setDialog((prevState) => ({ ...prevState, open: false }))
  }

  const handleOpenDialog = (property: TProperty) => {
    let inputValue = ''
    let inputType: TInputType = 'text'
    let inputOptions: Option[] = []

    switch (property) {
    case 'expanded_segment_levels':
      inputOptions = expandedSegmentLevelOptions
      inputType = 'select'
      inputValue = expandedSegmentLevels.toString()
      break
    case 'label_chars':
      inputType = 'number'
      inputValue = widget.settings.label_chars?.toString() ?? ''
      break
    case 'segment_by':
      inputOptions = getSegmentByOptions(
        segmentAttributeOptions,
        t('global', 'ai_generated')
      )
      inputType = 'select'
      inputValue = widget.settings.segment_by.selected ?? ''
      break
    case 'segment_by_display_name':
      inputType = 'text'
      inputValue = widget.settings.segment_by.display_name ?? ''
      break
    case 'top_n_limit':
      inputType = 'number'
      inputValue = widget.settings.top_n_limit?.toString() ?? ''
      break
    case 'sort_by':
      inputOptions = widget.settings.sort_by.options.map((option) => ({
        id: option.value?.toString(),
        title: option.label
      }))
      inputType = 'select'
      inputValue = widget.settings.sort_by.selected?.toString() ?? ''
      break
    case 'visualization_constraint':
      inputOptions = sortOrderOptions
      inputType = 'select'
      inputValue = widget.settings.sort_by.visualization_constraint ?? ''
      break
    }

    setDialog({
      open: true,
      property: property,
      title: getPropertyTitle(property),
      data: {
        options: inputOptions,
        type: inputType,
        value: inputValue
      }
    })
  }

  const handleEditDialogValue = (value: string) => {
    setDialog((prevState) => ({
      ...prevState,
      data: { ...prevState.data, value }
    }))
  }

  const handleSubmitDialog = () => {
    let body: InnerWidgetPatchBody = {}

    switch (dialog.property) {
    case 'expanded_segment_levels':
      body = { expanded_segment_levels: parseInt(dialog.data.value) }
      break
    case 'label_chars':
      body = {
        label_chars: dialog.data.value ? parseInt(dialog.data.value) : null
      }
      break
    case 'segment_by':
      body = { segment_by: dialog.data.value }
      break
    case 'segment_by_display_name':
      body = {
        segment_by_display_name: dialog.data.value ? dialog.data.value : null
      }
      break
    case 'top_n_limit':
      body = {
        top_n_limit: dialog.data.value ? parseInt(dialog.data.value) : null
      }
      break
    case 'sort_by':
      const value = (
          dialog.data.value === 'true' ? true : dialog.data.value
        ) as string | true

      // get selected option
      const option = widget.settings.sort_by.options.find(
        (option) => option.value === value
      ) as TSortByOption

      body = {
        [option.key]: value
      }
      break
    case 'visualization_constraint':
      body = {
        visualization_constraint: dialog.data.value as VisualizationConstraint
      }
      break
    }

    updateWidget(body)
  }

  return (
    <>
      <SettingsGroup title={getSegmentTitle(widget.settings.type.selected)}>
        <SettingsItem
          onClick={handleOpenSegmentByDrawer}
          title={getPropertyTitle('segment_by')}
          valueAdornments={
            segmentBy?.ai_generated ? (
              <AiIconTip title={t('global', 'ai_generated')} />
            ) : undefined
          }
          value={selectedLabel}
        />
        {[
          WidgetType.BAR_CHART,
          WidgetType.COMBO_CHART,
          WidgetType.LIST,
          WidgetType.BUBBLE_CHART
        ].includes(widget.settings.type.selected) &&
        widget.settings.sort_by.available ? (
              <>
                <SettingsItem
                  onClick={handleOpenSortByDrawer}
                  title={getPropertyTitle('sorting')}
                  value={sortingLabel}
                />
                <SettingsItem
                  onClick={handleOpenLimitationsDrawer}
                  title={getPropertyTitle('limitations')}
                />
              </>
            ) : null}
      </SettingsGroup>

      {/*-- drawer for segment by --*/}
      <SettingsDrawer
        open={segmentByDrawer}
        onBack={handleBackSegmentByDrawer}
        onClose={handleCloseSegmentByDrawer}
        title={getPropertyTitle('segment_by')}
      >
        <SettingsGroup>
          <SettingsItem
            onClick={() =>
              variantOptionsEligible && isCoreWidget(widget)
                ? handleOpenSourceDialog()
                : handleOpenDialog('segment_by')
            }
            title={getPropertyTitle('segment_by')}
            value={selectedLabel}
            valueAdornments={
              !variantMode && segmentBy?.ai_generated ? (
                <AiIconTip title={t('global', 'ai_generated')} />
              ) : undefined
            }
            variant="edit"
          />
          {widget.settings.type.selected === WidgetType.LIST &&
          isCoreWidget(widget) ? (
                <SettingsItem
                  actions={
                    <Switch
                      checked={
                        !variantMode && widget.settings.external_link.show
                      }
                      disabled={variantMode}
                      onChange={() =>
                        updateWidget({
                          // eslint-disable-next-line max-len
                          show_external_link: !widget.settings.external_link.show
                        })
                      }
                      size="small"
                    />
                  }
                  disabled={
                    variantMode || !widget.settings.external_link.available
                  }
                  disabledText={
                    variantMode
                      ? t('widgets', 'linking_unavailable_for_variant_options')
                      : t('widgets', 'linking_unavailable_for_segmentation')
                  }
                  title={t('widgets', 'link_to_source_system')}
                />
              ) : null}
        </SettingsGroup>

        {variantMode && isCoreWidget(widget) ? (
          <VariantOptionSection
            createVariantOptionBy={createVariantOptionBy}
            deleteVariantOptionBy={deleteVariantOptionBy}
            onClose={onClose}
            updateVariantOptionBy={updateVariantOptionBy}
            updateWidget={updateWidget}
            widget={widget}
          />
        ) : null}

        <SettingsGroup helperText={t('widgets', 'label_chars_helper_text')}>
          <SettingsItem
            onClick={() => handleOpenDialog('segment_by_display_name')}
            title={getPropertyTitle('segment_by_display_name')}
            value={widget.settings.segment_by.display_name}
            variant="edit"
          />
          {[
            WidgetType.BAR_CHART,
            WidgetType.COMBO_CHART,
            WidgetType.LINE_CHART,
            WidgetType.LIST
          ].includes(widget.settings.type.selected) ? (
                <SettingsItem
                  onClick={() => handleOpenDialog('label_chars')}
                  title={getPropertyTitle('label_chars')}
                  value={widget.settings.label_chars}
                  variant="edit"
                />
              ) : null}
        </SettingsGroup>

        {isCoreWidget(widget) ? (
          <AdditionalSegmentSection
            createAdditionalSegmentBy={createAdditionalSegmentBy}
            deleteAdditionalSegmentBy={deleteAdditionalSegmentBy}
            onClose={onClose}
            updateAdditionalSegmentBy={updateAdditionalSegmentBy}
            widget={widget}
          />
        ) : null}

        {widget.settings.type.selected === WidgetType.LIST &&
        isCoreWidget(widget) &&
        lastRowLevel > 0 ? (
              <SettingsGroup
                helperText={t('widgets', 'expanded_segment_levels_helper_text')}
              >
                <SettingsItem
                  onClick={() => handleOpenDialog('expanded_segment_levels')}
                  title={getPropertyTitle('expanded_segment_levels')}
                  value={rowLevelNames[expandedSegmentLevels]}
                  variant="edit"
                />
              </SettingsGroup>
            ) : null}
      </SettingsDrawer>

      {/*-- drawer for limitations --*/}
      <SettingsDrawer
        open={limitationsDrawer}
        onBack={handleBackLimitationsDrawer}
        onClose={handleCloseLimitationsDrawer}
        title={getPropertyTitle('limitations')}
      >
        {[
          WidgetType.BAR_CHART,
          WidgetType.COMBO_CHART,
          WidgetType.LIST
        ].includes(widget.settings.type.selected) &&
        widget.class !== WidgetClass.EXPORT_TABLE ? (
              <SettingsGroup helperText={t('widgets', 'top_n_helper_text')}>
                <SettingsItem
                  onClick={() => handleOpenDialog('top_n_limit')}
                  title={getPropertyTitle('top_n_limit')}
                  value={widget.settings.top_n_limit}
                  variant="edit"
                />
              </SettingsGroup>
            ) : null}

        <SettingsGroup
          helperText={t('widgets', 'limit_selection_to_primary_kpi_helper')}
        >
          <SettingsItem
            actions={
              <Switch
                checked={widget.settings.limit_selection_to_primary_kpi}
                onChange={() =>
                  updateWidget({
                    limit_selection_to_primary_kpi:
                      !widget.settings.limit_selection_to_primary_kpi
                  })
                }
                size="small"
              />
            }
            title={getPropertyTitle('limit_selection_to_primary_kpi')}
          />
        </SettingsGroup>
      </SettingsDrawer>

      {/*-- drawer for sort --*/}
      <SettingsDrawer
        open={sortByDrawer}
        onBack={handleBackSortByDrawer}
        onClose={handleCloseSortByDrawer}
        title={getPropertyTitle('sorting')}
      >
        <SettingsGroup>
          <SettingsItem
            onClick={() => handleOpenDialog('sort_by')}
            title={getPropertyTitle('sort_by')}
            value={selectedSortByLabel}
            variant="edit"
          />
          <SettingsItem
            onClick={() => handleOpenDialog('visualization_constraint')}
            title={getPropertyTitle('visualization_constraint')}
            value={selectedSortOrderLabel}
            variant="edit"
          />
        </SettingsGroup>
      </SettingsDrawer>

      {/*-- dialogs --*/}
      <SimpleDialog
        actions={
          <>
            <Button onClick={handleCloseSourceDialog} variant="text">
              {t('global', 'cancel')}
            </Button>
            <Button
              disabled={!sourceDialogHasChange}
              onClick={handleSubmitSourceDialog}
            >
              {t('global', 'save')}
            </Button>
          </>
        }
        helperText={t('widgets', 'variant_options_mode_helper')}
        onClose={handleCloseSourceDialog}
        open={sourceDialog.open}
        sx={{
          zIndex: 1302 // above the settings drawer, like SettingsEditDialog
        }}
        title={getPropertyTitle('segment_by')}
      >
        <ToggleButton
          items={[
            {
              title: t('widgets', 'variant_options_mode_attribute'),
              value: SegmentSource.ATTRIBUTE
            },
            {
              title: t('widgets', 'variant_options_mode_variants'),
              value: SegmentSource.VARIANTS
            }
          ]}
          onChange={(_, value) =>
            setSourceDialog((prevState) => ({
              ...prevState,
              source: value ?? prevState.source
            }))
          }
          size="small"
          sx={{ mb: 2, mt: 1 }}
          value={sourceDialog.source}
        />
        {sourceDialog.source === SegmentSource.ATTRIBUTE ? (
          <>
            <SelectSearch
              closeOnSelect
              multiple={false}
              noGutter={true}
              onChange={(option) =>
                setSourceDialog((prevState) => ({
                  ...prevState,
                  attributeId:
                    (option?.id as string) ?? prevState.attributeId
                }))
              }
              options={getSegmentByOptions(
                segmentAttributeOptions,
                t('global', 'ai_generated')
              )}
              selected={
                getSegmentByOptions(
                  segmentAttributeOptions,
                  t('global', 'ai_generated')
                ).find((option) => option.id === sourceDialog.attributeId) ??
                null
              }
              size={'medium'}
            />
            {variantMode ? (
              <Text color="text.secondary" sx={{ mt: 2 }} variant="body2">
                {t('widgets', 'variant_options_disable_body')}
              </Text>
            ) : null}
          </>
        ) : (
          <>
            <Text color="text.secondary" variant="body2">
              {t('widgets', 'variant_options_dialog_info')}
            </Text>
            {!variantMode ? (
              <Text color="text.secondary" sx={{ mt: 1.5 }} variant="body2">
                {t('widgets', 'variant_options_dialog_start')}
              </Text>
            ) : null}
          </>
        )}
      </SimpleDialog>
      <SettingsEditDialog
        onChange={handleEditDialogValue}
        onClose={handleCloseDialog}
        onSubmit={handleSubmitDialog}
        open={dialog.open}
        options={dialog.data.options}
        title={dialog.title}
        type={dialog.data.type}
        value={dialog.data.value}
      />
    </>
  )
}

export default SegmentSection
